import type { RoomsParticipantDistribution } from '@/domain/shared/types'
import type { SearchResult } from '@/scopes/search/search/infrastructure/api/types'
import type { Collection } from './CollectionTag'
import { useIntl } from '@/intl'
import { Card } from '@/scopes/common/components/Card'
import { PriceInfo } from '@/scopes/common/components/PriceInfo'
import { useBreakpoints } from '@/scopes/common/components/utils/breakpoints'
import { useOptimizelyFlagContext } from '@/scopes/common/infrastructure/ab-testing/useOptimizely'
import { transformIncludedServices } from '@/scopes/search/search/domain/product-card/transformIncludedServices'
import { CampaignUsp } from '@/scopes/search/search/pages/SearchPage/components/Results/ProductCard/CampaignUsp'
import { ProductCardImage } from '@/scopes/search/search/pages/SearchPage/components/Results/ProductCardLegacy/ProductCardImage'

import { useDecision } from '@optimizely/react-sdk'
import {
  BulletList,
  mqMin,
  space,
  tok,
  tokPx,
} from '@sunwebgroup/ui-components'
import { Button, focusOutline, Icon, Tag, Typography } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { css, styled } from 'styled-components'
import * as List from '../ListItem'
import { CollectionTag } from './CollectionTag'
import FavoriteButton from './FavoriteButton'
import { ImageCarousel } from './ImageCarousel'
import { LocationTag } from './LocationTag'
import { OverlayLink } from './OverlayLink'
import { ScoreRatings } from './ScoresRatings'

export interface ProductCardProps {
  result: SearchResult
  participants?: RoomsParticipantDistribution
  onClick: () => void
}

const LIST_ITEM_ICON_SIZE = 16

const PriceBoxWrapper = styled.div``

const StyledProductSearchResultCard = styled(Card)`
  color: ${tok('semantics_content_color')};
  position: relative;
  ${focusOutline()}

  ${PriceBoxWrapper} {
    ${Tag} {
      position: absolute;
      transform: translateY(-100%);
      pointer-events: none; // make it possible to click on the link
    }
  }
`

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 240px;

  > ${Tag} {
    left: ${space('primitives_space_12')};
    position: absolute;
    pointer-events: none;
    top: ${space('primitives_space_12')};
    max-width: 75%;

    :last-child {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  ${FavoriteButton} {
    position: absolute;
    top: ${space('primitives_space_16')};
    right: ${space('primitives_space_16')};
  }

  @media (hover: hover) {
    &:hover {
      button {
        display: inline-flex;
      }
    }
  }
`

const TextsWrapper = styled.div`
  display: grid;
  flex: 1;
  padding: ${space('primitives_space_20')};
  ${focusOutline()}
`

const UnorderedList = styled(List.Root)`
  margin-top: ${space('primitives_space_20')};
  margin-bottom: ${space('primitives_space_24')};
`

const PackageBlockWrapper = styled.div``

const PackageBlockAndPriceBoxWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`

const UnavailableBanner = styled.div`
  width: 100%;
  background-color: ${tok('primitives_color_warning_50')};
  border-radius: ${tokPx('components_banner_corner_radius')};
  border: ${tokPx('components_banner_border_width')} solid ${tok('primitives_color_warning_150')};
  gap: ${space('primitives_space_8')};
  padding: ${space('primitives_space_12')};
`

const AirportInfo = styled.div`
  display: flex;
  gap: ${space('primitives_space_8')};
  align-items: center ;
  color: ${tok('semantics_content_secondary_color')};
`

const StyledCollectionTag = styled(CollectionTag)`
  position: absolute;
  top: ${space('primitives_space_12')};
  left: ${space('primitives_space_12')};
  pointer-events: none;
`

ProductCard.modSize = (minWidth: 'tablet' | 'desktop') => {
  switch (minWidth) {
    case 'tablet':
      return css`
        ${ImageWrapper} {
          max-width: 255px;
          height: auto;
        }

        ${StyledProductSearchResultCard} {
          display: flex;
        }

        ${UnorderedList} {
          max-width: 75%;
        }
      `
    case 'desktop':
      return css`
        ${ImageWrapper} {
          flex-shrink: 0;
          max-width: 320px;
          height: auto;
        }
      `
  }
}

const AccoName = styled('Typography')`
  ${Typography.modVariant('display-i-regular')};

  @media ${mqMin('primitives_breakpoint_m_min')} {
    ${Typography.modVariant('display-g-regular')};
  }
`

const Item = styled(List.Item)`
  align-items: center;
`

const ProductCardActions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  align-content: flex-start;
  gap: ${space('primitives_space_8')};
  align-self: stretch;
  flex-wrap: wrap;

  margin-top: ${space('primitives_space_16')};

  ${Button} {
    min-width: ${tokPx('primitives_area_size_160')};
  }
`

export function ProductCard({
  result,
  participants,
  onClick,
}: ProductCardProps): JSX.Element {
  const {
    id: accommodationId,
    price,
    featuredFilters,
    arrivalDateDetail: arrivalDate,
    departureDateDetail: departureDate,
    campaign,
    urlWithFilters: href,
    images: imageSources,
    isUnavailable,
    locations,
    name = '',
    rating = 0,
    userRating,
    userRatingText,
    usps,
    outboundDepartureAirportName,
    collection,
    isMarketExclusive,
    isSustainable,
  } = result
  const accommodationServices = transformIncludedServices(price?.includedServices)
  const departureDateRaw = price?.acmInformation?.departureDateRaw
  const durationRaw = price?.acmInformation?.durationRaw
  const yieldCampaign = price?.yieldCampaignName
  const averagePrice = price?.averagePrice
  const transportType = price?.acmInformation?.transportType
  const priceDiscounts = price?.priceDiscounts
  const partyComposition = featuredFilters?.occupancy
  const mealPlan = featuredFilters?.mealplan

  const intl = useIntl()
  const { isDesktopSm } = useBreakpoints()
  const collectionType = (collection?.id.toLowerCase() || 'default') as Collection
  const { hideCarousel } = useOptimizelyFlagContext()
  const [{ enabled: showCTA }] = useDecision('ID_385_bring_back_the_button_in_the_cards_on_plp')
  return (
    <StyledProductSearchResultCard qa-product-card="true" data-qa="product-card" onClick={onClick}>
      {/* OverlayLink is intentionally empty, because the whole card should be clickable, but also the image carousel should be scrollable & buttons clickable */}
      <OverlayLink href={href} aria-label={name} target={isDesktopSm ? '_blank' : '_self'} />
      <ImageWrapper>
        {hideCarousel
          ? (
              <ProductCardImage href={href} src={imageSources[0]} alt={name} />
            )
          : (
              <ImageCarousel
                imageIndex={accommodationId}
                images={imageSources.map(imageSrc => ({
                  src: imageSrc,
                  alt: name,
                }))}
                href={href}
              />
            )}
        {yieldCampaign
          ? (
              <Tag variant="primary">
                {yieldCampaign}
              </Tag>
            )
          : (
              collection && (
                <StyledCollectionTag collectionName={collectionType}>
                  {collection.name}
                </StyledCollectionTag>
              )
            )}
        <FavoriteButton productId={accommodationId} />
      </ImageWrapper>
      <TextsWrapper>
        <AccoName as="span">
          {name}
        </AccoName>
        <ScoreRatings rating={rating} userRating={userRating} aria-label={userRatingText} />
        <LocationTag locations={locations} />
        <UnorderedList>
          { /** TODO: Migrate this one to uds once the icons in Sitecore are updated to use the UDS ones. */}
          {accommodationServices.map((service, index) => (
            <BulletList.Item $icon={service.icon} key={index}>
              {service.text}
            </BulletList.Item>
          ))}
          {isMarketExclusive && (
            <Item $icon="LogoSunweb" $size={LIST_ITEM_ICON_SIZE} data-qa="sunweb-only-usp">
              <Typography $variant="body-s-regular-tight">{intl.formatMessage({ id: 'product_card_sunweb_exclusive' })}</Typography>
            </Item>
          )}
          {isSustainable && (
            <Item $icon="Leaf" $size={LIST_ITEM_ICON_SIZE} data-qa="sustainability-usp">
              <Typography $variant="body-s-regular-tight">{intl.formatMessage({ id: 'product_card_sustainable' })}</Typography>
            </Item>
          )}
          <CampaignUsp campaign={campaign} />
          {usps.map((uspText, index) => (
            <List.Item data-qa="acco-usp" $size={LIST_ITEM_ICON_SIZE} key={index}>
              {uspText}
            </List.Item>
          ))}
        </UnorderedList>
        {isUnavailable
          ? (
              <UnavailableBanner>
                <Typography $variant="body-m-bold-snug">
                  {intl.formatMessage({
                    id: 'product_card_unavailable_banner_text',
                  })}
                </Typography>
              </UnavailableBanner>
            )
          : (
              <PackageBlockAndPriceBoxWrapper>
                <PackageBlockWrapper>
                  <Typography as="div" $variant="body-s-bold-snug">
                    {departureDate}
                    {' '}
                    -
                    {' '}
                    {arrivalDate}
                  </Typography>
                  {mealPlan && partyComposition && (
                    <Typography as="div" $variant="body-s-regular-snug">
                      {mealPlan}
                      ,
                      {' '}
                      {partyComposition}
                    </Typography>
                  )}
                  {outboundDepartureAirportName && (
                    <AirportInfo>
                      <Icon glyph="AirplaneTakingOff" size={LIST_ITEM_ICON_SIZE} />
                      <Typography as="div" $variant="body-s-regular-snug">
                        {outboundDepartureAirportName}
                      </Typography>
                    </AirportInfo>
                  )}
                </PackageBlockWrapper>
                {averagePrice && (
                  <PriceBoxWrapper>
                    <PriceInfo
                      accommodationId={accommodationId}
                      departureDateRaw={departureDateRaw}
                      durationRaw={durationRaw}
                      mealPlan={mealPlan}
                      price={averagePrice}
                      priceDiscounts={priceDiscounts}
                      transportType={transportType}
                      participants={participants}
                    />
                  </PriceBoxWrapper>
                )}
              </PackageBlockAndPriceBoxWrapper>
            )}
        {showCTA && (
          <ProductCardActions>
            <Button>View</Button>
          </ProductCardActions>
        )}
      </TextsWrapper>
    </StyledProductSearchResultCard>
  )
}

ProductCard.displayName = 'ProductCard'
